import { DownloadIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Flex,
  Grid,
  Heading,
  IconButton,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { CsvExportHelper } from 'classes/helpers/csv-export.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import { MachineSettingsDateRangeInput } from 'components/sections/admin-portal/machines/dialogs/settings/common/machine-settings-date-range-input';
import { MachineSessionDetails } from 'components/sections/admin-portal/machines/dialogs/settings/machine-session-history/machine-session-details';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { SessionEventsContext } from 'contexts/session-events.context';
import {
  endOfDay,
  endOfToday,
  startOfDay,
  startOfToday,
  subMonths,
} from 'date-fns';
import { TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMachineSettingsDateFilter } from 'interfaces/i-machine-settings';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { orderBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';

const DEFAULT_END_DATE = endOfToday();
const DEFAULT_START_DATE = subMonths(startOfToday(), 3);
const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

interface IMachineSessionHistoryFilters {
  sessions: string[];
  users: string[];
  dateRange: IMachineSettingsDateFilter;
}

interface ISessionSummaryRow extends ISessionSummary, RowWithID {}

export const MachineSessionHistory = () => {
  const current = useAuthStore((s) => s.current);
  const machine = useMachinesPortalStore((s) => s.machine);

  const { getShotsData } = useContext(SessionEventsContext);

  const [loading, setLoading] = useState(false);
  const [sessionDownloadInProgressIDs, setSessionDownloadProgressIDs] =
    useState<string[]>([]);

  const [sessions, setSessions] = useState<ISessionSummaryRow[]>([]);
  const [selectedSession, setSelectedSession] = useState<ISessionSummaryRow>();
  const [filter, setFilter] = useState<IMachineSessionHistoryFilters>({
    // local filter
    sessions: [],
    // local filter
    users: [],

    // server filter
    dateRange: {
      start: DEFAULT_START_DATE,
      end: DEFAULT_END_DATE,
    },
  });

  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    const fetchMachineSessions = async () => {
      try {
        setLoading(true);
        const result = await SessionEventsService.summarizeMachinesSessions({
          machineIDs: [machine.machineID],
          start_date: filter.dateRange.start.toISOString(),
          end_date: filter.dateRange.end.toISOString(),
        });

        const sessionsWithIDs = StringHelper.addIds(result, 'sessionID');

        setSessions(orderBy(sessionsWithIDs, ['startDate'], ['desc']));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchMachineSessions();
  }, [machine?.machineID, filter.dateRange.start, filter.dateRange.end]);

  // to be updated with non-shot data?
  const downloadSession = async (sessionID: string) => {
    if (!machine) {
      return;
    }

    setSessionDownloadProgressIDs((prev) => [...prev, sessionID]);

    const sessionRows = await getShotsData({
      machineID: machine.machineID,
      session: sessionID,
      debugTracking:
        current.role === UserRole.admin || machine.enable_tracking_debug,
      silently: true,
      szType: machine.strikeZoneType,
    });

    const filenamePrefix = slugify(
      [machine.machineID, sessionID ?? '']
        .map((m) => m?.trim())
        .filter((m) => m)
        .join('-'),
      { strict: true }
    );

    await CsvExportHelper.saveCsv(sessionRows, {
      maxRows: 200,
      prefix: filenamePrefix,
    });

    setSessionDownloadProgressIDs((prev) =>
      prev.filter((m) => m !== sessionID)
    );
  };

  const columns = useMemo<ITypedTableColumn<ISessionSummaryRow>[]>(
    () => [
      {
        label: 'settings.user',
        key: 'user',
        formatFn: (s) => <Text>{s.user}</Text>,
      },
      {
        label: 'common.session',
        key: 'sessionID',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.sessionID, b.sessionID) * dir,
        formatFn: (s) => (
          <CommonTooltip
            text="Click to copy this session ID to the clipboard"
            trigger={
              <Badge
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  navigator.clipboard.writeText(s.sessionID);
                  NotifyHelper.success({
                    message_md: 'Session ID copied to clipboard!',
                  });
                }}
                style={{
                  maxWidth: '100%',
                }}
              >
                <Text className="font-mono" truncate>
                  {s.sessionID}
                </Text>
              </Badge>
            }
          />
        ),
      },
      {
        label: 'common.shots',
        key: 'totalFires',
        align: 'right',
      },
      {
        label: 'common.start-date',
        key: 'startDate',
        dataType: 'datetime',
      },
      {
        label: '',
        key: 'download',
        align: 'right',
        formatFn: (s) => (
          <IconButton
            color={RADIX.COLOR.ACCENT}
            variant="soft"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              downloadSession(s.sessionID);
            }}
          >
            {sessionDownloadInProgressIDs.includes(s.sessionID) ? (
              <Spinner />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
        ),
      },
    ],
    [sessionDownloadInProgressIDs]
  );

  const options = useMemo(() => {
    const sessionIDs = ArrayHelper.unique(sessions.map((m) => m.sessionID)).map(
      (m) => {
        const o: IOption = {
          label: m,
          value: m,
          className: 'font-mono',
        };

        return o;
      }
    );

    const users = ArrayHelper.unique(sessions.map((m) => m.user)).map((m) => ({
      label: m,
      value: m,
    }));

    return { sessionIDs, users };
  }, [sessions]);

  const filtered = useMemo(() => {
    return sessions
      .filter((m) => {
        if (!filter.users.length) {
          return true;
        }
        return filter.users.includes(m.user);
      })
      .filter((m) => {
        if (!filter.sessions.length) {
          return true;
        }
        return filter.sessions.includes(m.sessionID);
      });
  }, [sessions, filter.users, filter.sessions]);

  const applyFilter = (v: Partial<IMachineSessionHistoryFilters>) => {
    setFilter((prev) => ({
      ...prev,
      ...v,
    }));
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'start',
      dir: 1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: (model?: ISessionSummaryRow) =>
      setSelectedSession(model),
  };

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminMachineSessionHistory,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  return (
    <ErrorBoundary identifier="MachineSessionHistory">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        {selectedSession && (
          <MachineSessionDetails
            session={selectedSession}
            onBack={() => setSelectedSession(undefined)}
            onExportSession={() => downloadSession(selectedSession.sessionID)}
            exportInProgress={sessionDownloadInProgressIDs.includes(
              selectedSession.sessionID
            )}
          />
        )}
        <Flex
          direction="column"
          gap={RADIX.FLEX.GAP.MD}
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedSession ? 'hidden' : '',
          ])}
        >
          <Heading size="4">{t('common.session-history')}</Heading>

          <FlexTableWrapper
            gap={RADIX.FLEX.GAP.FORM}
            header={
              <Grid columns="3" gap={RADIX.FLEX.GAP.SM}>
                <Box gridColumn="span 1">
                  <CommonSearchInput
                    id="machine-session-user-filter"
                    placeholder="settings.user"
                    options={options.users}
                    values={filter.users}
                    onChange={(v) => applyFilter({ users: v })}
                    multiple
                    strict
                  />
                </Box>
                <Box gridColumn="span 1">
                  <CommonSearchInput
                    id="machine-session-id-filter"
                    placeholder="admin.session-id"
                    options={options.sessionIDs}
                    values={filter.sessions}
                    onChange={(v) => applyFilter({ sessions: v })}
                    multiple
                    strict
                  />
                </Box>
                <Box gridColumn="span 1">
                  <MachineSettingsDateRangeInput
                    dateRange={filter.dateRange}
                    onChange={(dateRange) => {
                      if (!dateRange) {
                        applyFilter({ dateRange: undefined });
                        return;
                      }

                      applyFilter({
                        dateRange: {
                          start: startOfDay(dateRange.start),
                          end: endOfDay(dateRange.end),
                        },
                      });
                    }}
                    optional={false}
                  />
                </Box>
              </Grid>
            }
            table={
              <CommonTableHoC
                dataTestID="SessionHistory"
                identifier="SessionHistoryTable"
                displayColumns={columns}
                displayData={filtered}
                loading={loading}
                {...pagination}
                {...select}
                {...sort}
                vFlex
              />
            }
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
